import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api.js'
import { Plus, Archive, FileText, Download, Trash2, Edit, X, Clock, CheckCircle, Search, Filter, Upload, ShieldCheck, MessageSquare, AlertCircle, Eye, ExternalLink } from 'lucide-react'
import { ConfirmModal } from '../components/ConfirmModal'
import { useAuth } from '../context/AuthContext.jsx'

function FilePreviewModal({ doc, onClose }) {
  const isImage = doc.content_type?.startsWith('image/')
  const isPdf = doc.content_type === 'application/pdf'
  const hasData = !!doc.file_data
  const [blobUrl, setBlobUrl] = useState(null)

  useEffect(() => {
    let url;
    if (isPdf && hasData && doc.file_data) {
      try {
        const b64Data = doc.file_data.replace(/\s/g, '');
        const byteCharacters = atob(b64Data);
        const byteArrays = [];
        for (let offset = 0; offset < byteCharacters.length; offset += 512) {
          const slice = byteCharacters.slice(offset, offset + 512);
          const byteNumbers = new Array(slice.length);
          for (let i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i);
          }
          byteArrays.push(new Uint8Array(byteNumbers));
        }
        const blob = new Blob(byteArrays, { type: 'application/pdf' });
        url = URL.createObjectURL(blob);
        setBlobUrl(url);
      } catch (e) {
        console.error("Failed to create blob URL for PDF", e);
      }
    }
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [doc.file_data, isPdf, hasData]);

  const handleDownload = () => {
    if (blobUrl) {
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = doc.file_url || `${doc.title}.pdf`;
      a.click();
    } else if (hasData) {
      const a = document.createElement('a');
      a.href = `data:${doc.content_type};base64,${doc.file_data}`;
      a.download = doc.file_url || `${doc.title}`;
      a.click();
    }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 1000, width: '95%', height: '90vh', background: 'var(--bg-elevated)', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <FileText size={20} color="var(--accent)" />
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <h2 style={{ fontSize: '1rem' }}>{doc.title}</h2>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>v{doc.version} • {doc.category} Viewer</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-ghost btn-sm" style={{ padding: 6, color: 'var(--accent)' }} onClick={handleDownload} title="Download File"><Download size={16} /></button>
            <button className="btn btn-ghost btn-sm" style={{ padding: 6 }} onClick={onClose}><X size={16} /></button>
          </div>
        </div>
        <div className="modal-body" style={{ flex: 1, overflow: 'hidden', padding: 0, background: '#525659' }}>
          {hasData ? (
            isPdf ? (
              blobUrl ? (
                <iframe 
                  src={`${blobUrl}#view=FitH`}
                  style={{ width: '100%', height: '100%', border: 'none' }} 
                  title={doc.title}
                />
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'white' }}>
                  Loading PDF...
                </div>
              )
            ) : isImage ? (
              <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'auto', padding: 20 }}>
                <img src={`data:${doc.content_type};base64,${doc.file_data}`} alt={doc.title} style={{ maxWidth: '100%', height: 'auto', boxShadow: '0 4px 20px rgba(0,0,0,0.3)' }} />
              </div>
            ) : (
              <div style={{ padding: 40, color: 'white', textAlign: 'center' }}>
                <AlertCircle size={48} style={{ marginBottom: 16, opacity: 0.5 }} />
                <p>Preview not available for this file type ({doc.content_type}).</p>
                <p style={{ fontSize: '0.875rem', marginTop: 8, opacity: 0.7 }}>Please download the file to view its content.</p>
              </div>
            )
          ) : (
            <div style={{ padding: 40, color: 'white', textAlign: 'center' }}>
              <Archive size={48} style={{ marginBottom: 16, opacity: 0.5 }} />
              <p>No actual file content found for this record.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function DocDetailsModal({ doc, onClose }) {
  const { data: history = [], isLoading } = useQuery({
    queryKey: ['documents', doc.id, 'reviews'],
    queryFn: () => api.getDocReviews(doc.id)
  })

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 600 }}>
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <FileText size={20} color="var(--accent)" />
            <h2>Document Details & History</h2>
          </div>
          <button className="btn btn-ghost btn-sm" style={{ padding: 6 }} onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body" style={{ maxHeight: '80vh', overflow: 'auto' }}>
          <div style={{ background: 'var(--bg-elevated)', padding: 16, borderRadius: 12, marginBottom: 20 }}>
            <h3 style={{ fontSize: '0.9375rem', fontWeight: 600, marginBottom: 4 }}>{doc.title}</h3>
            <div className="flex items-center gap-8" style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              <span>{doc.category}</span>
              <span>•</span>
              <span>v{doc.version}</span>
              <span>•</span>
              <span>Owner: {doc.owner}</span>
            </div>
            <div style={{ marginTop: 12, fontSize: '0.75rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Download size={12} /> Reference ID: <span className="font-mono" style={{ color: 'var(--accent)' }}>{doc.file_url || 'local_db_ref_001'}</span>
            </div>
          </div>

          <div>
            <h4 style={{ fontSize: '0.8125rem', fontWeight: 600, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-secondary)' }}>
              <Clock size={14} /> Audit Trail & Review History
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {isLoading && <p className="text-muted text-xs">Loading history...</p>}
              {history.map((rev, i) => (
                <div key={i} style={{ borderLeft: '2px solid var(--border)', paddingLeft: 16, position: 'relative' }}>
                  <div style={{
                    width: 8, height: 8, borderRadius: '50%', background: rev.status === 'Approved' ? 'var(--success)' : 'var(--warning)',
                    position: 'absolute', left: -5, top: 4
                  }} />
                  <div className="flex items-center justify-between" style={{ marginBottom: 4 }}>
                    <span style={{ fontSize: '0.75rem', fontWeight: 600, color: rev.status === 'Approved' ? 'var(--success)' : 'var(--warning)' }}>{rev.status}</span>
                    <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
                      {new Date(rev.reviewed_at).toLocaleDateString()} • {rev.reviewer}
                    </span>
                  </div>
                  <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', lineHeight: 1.4 }}>{rev.comments || 'No comments provided.'}</p>
                </div>
              ))}
              {!isLoading && history.length === 0 && <p className="text-muted text-xs">No prior reviews found.</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function DocReviewModal({ doc, onClose, onReview, isReviewing }) {
  const [comment, setComment] = useState('')
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 600 }}>
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <ShieldCheck size={20} color="var(--accent)" />
            <h2>Submit Document Review</h2>
          </div>
          <button className="btn btn-ghost btn-sm" style={{ padding: 6 }} onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          <div style={{ background: 'var(--bg-elevated)', padding: 12, borderRadius: 8, marginBottom: 16 }}>
            <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 2 }}>Reviewing:</p>
            <p style={{ fontSize: '0.875rem', fontWeight: 600 }}>{doc.title} (v{doc.version})</p>
          </div>
          <div className="form-group">
            <label className="form-label">Review Feedback / Comments</label>
            <textarea
              className="form-input"
              style={{ minHeight: 120, fontSize: '0.875rem' }}
              placeholder="Detail what improvements are required or provide approval justification..."
              value={comment}
              onChange={e => setComment(e.target.value)}
            />
          </div>
          <div style={{ display: 'flex', gap: 24, marginTop: 24, marginBottom: 12 }}>
            <button
              className="btn btn-primary"
              style={{ flex: 1 }}
              disabled={isReviewing}
              onClick={() => onReview({ status: 'Approved', comments: comment })}
            >
              Approve
            </button>
            <button
              className="btn btn-secondary"
              style={{ flex: 1, color: 'var(--danger)' }}
              disabled={isReviewing}
              onClick={() => onReview({ status: 'Rejected', comments: comment })}
            >
              Reject
            </button>
            <button
              className="btn btn-secondary"
              style={{ flex: 1, color: 'var(--warning)', borderColor: 'var(--warning-dim)' }}
              disabled={isReviewing}
              onClick={() => onReview({ status: 'Improvement Required', comments: comment })}
            >
              Request Improvement
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function SubmitReviewModal({ doc, users, onClose, onSubmit, isSubmitting }) {
  const [reviewerId, setReviewerId] = useState('')

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 500 }}>
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <MessageSquare size={20} color="var(--accent)" />
            <h2>Submit for Review</h2>
          </div>
          <button className="btn btn-ghost btn-sm" style={{ padding: 6 }} onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: 20 }}>
            Select an internal reviewer to evaluate <strong>{doc.title}</strong>. They will receive an email notification for this task.
          </p>
          <div className="form-group">
            <label className="form-label">Assign Reviewer</label>
            <select 
              className="form-select" 
              value={reviewerId} 
              onChange={e => setReviewerId(e.target.value)}
            >
              <option value="">Select a user...</option>
              {users.map(u => (
                <option key={u.id} value={u.id}>{u.full_name} ({u.designation || u.role})</option>
              ))}
            </select>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button 
            className="btn btn-primary" 
            disabled={!reviewerId || isSubmitting} 
            onClick={() => onSubmit(reviewerId)}
          >
            {isSubmitting ? 'Submitting...' : 'Send for Review'}
          </button>
        </div>
      </div>
    </div>
  )
}

function DocModal({ doc, onClose, onSave, isSaving }) {
  const [form, setForm] = useState(doc || { title: '', category: 'Policy', version: '1.0', status: 'Draft', owner: '' })
  const [files, setFiles] = useState([])
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const handleFileSelect = (e) => {
    const selected = Array.from(e.target.files)
    setFiles(selected)
    if (selected.length === 1 && !form.title) {
      set('title', selected[0].name.replace(/\.[^/.]+$/, ""))
    }
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h2>{doc ? 'Edit Document Reference' : 'Register Document'}</h2>
          <button className="btn btn-ghost btn-sm" style={{ padding: 6 }} onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          {!doc && (
            <div className="form-group">
              <label className="form-label">Select Files (Multiple supported)</label>
              <div
                style={{ border: '2px dashed var(--border)', borderRadius: 12, padding: 24, textAlign: 'center', background: 'var(--surface-hover)', cursor: 'pointer', position: 'relative' }}
                onClick={() => document.getElementById('doc-upload').click()}
              >
                <Upload size={24} color="var(--text-muted)" style={{ marginBottom: 8 }} />
                <div style={{ fontSize: '0.8125rem', fontWeight: 600 }}>{files.length > 0 ? `${files.length} files selected` : 'Click to browse files'}</div>
                <input type="file" id="doc-upload" multiple style={{ display: 'none' }} onChange={handleFileSelect} />
              </div>
              {files.length > 0 && (
                <div style={{ marginTop: 8, fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                  Files: {files.map(f => f.name).join(', ')}
                </div>
              )}
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Document Title *</label>
            <input className="form-input" value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Access Control Policy" />
            {files.length > 1 && <p style={{ fontSize: '0.75rem', color: 'var(--info)', marginTop: 4 }}>Note: Other files will use their filenames as titles.</p>}
          </div>
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">Category</label>
              <select className="form-select" value={form.category} onChange={e => set('category', e.target.value)}>
                {['Policy', 'Procedure', 'Standard', 'Record', 'Evidence'].map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Version</label>
              <input className="form-input" value={form.version} onChange={e => set('version', e.target.value)} placeholder="v1.0" />
            </div>
          </div>
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">Owner</label>
              <input className="form-input" value={form.owner} onChange={e => set('owner', e.target.value)} placeholder="Document Owner" />
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select 
                className="form-select" 
                value={form.status} 
                onChange={e => set('status', e.target.value)}
                disabled={form.category === 'Policy' || form.category === 'Procedure'}
              >
                {['Draft', 'Under Review', 'Approved', 'Needs Improvement', 'Obsolete'].map(s => <option key={s}>{s}</option>)}
              </select>
              {(form.category === 'Policy' || form.category === 'Procedure') && (
                <p style={{ fontSize: '0.65rem', color: 'var(--text-muted)', marginTop: 4 }}>Locked: Policies & Procedures must start as Draft.</p>
              )}
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={(!form.title && files.length === 0) || isSaving} onClick={async () => {
            if (doc) {
              onSave(form)
              return
            }

            const processFile = (file) => {
              return new Promise((resolve) => {
                const reader = new FileReader()
                reader.onload = () => {
                  const base64 = reader.result.split(',')[1]
                  resolve({
                    ...form,
                    title: files.length > 1 ? file.name.replace(/\.[^/.]+$/, "") : form.title,
                    file_url: file.name,
                    content_type: file.type,
                    file_data: base64
                  })
                }
                reader.readAsDataURL(file)
              })
            }

            if (files.length > 1) {
              const multi = await Promise.all(files.map(processFile))
              onSave(multi)
            } else if (files.length === 1) {
              const single = await processFile(files[0])
              onSave(single)
            } else {
              onSave({ ...form, file_url: null, content_type: null, file_data: null })
            }
          }}>
            {isSaving ? 'Processing…' : doc ? 'Update Reference' : 'Register Document'}
          </button>
        </div>
      </div>
    </div>
  )
}

export function Documents({ toast }) {
  const qc = useQueryClient()
  const { user } = useAuth()
  const [showModal, setShowModal] = useState(false)
  const [viewing, setViewing] = useState(null)
  const [previewing, setPreviewing] = useState(null)
  const [editing, setEditing] = useState(null)
  const [reviewing, setReviewing] = useState(null)
  const [submitting, setSubmitting] = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [search, setSearch] = useState('')

  const canReview = user?.role === 'Admin' || user?.role === 'Manager'

  const { data: docs = [], isLoading } = useQuery({ queryKey: ['documents'], queryFn: api.getDocuments })
  const { data: users = [] } = useQuery({ queryKey: ['users'], queryFn: api.getUsers })

  const createMut = useMutation({
    mutationFn: api.createDocument,
    onSuccess: () => { qc.invalidateQueries(['documents']); setShowModal(false); toast('Documents registered', 'success') },
    onError: e => toast(e.message, 'error'),
  })

  const updateMut = useMutation({
    mutationFn: ({ id, data }) => api.updateDocument(id, data),
    onSuccess: () => { qc.invalidateQueries(['documents']); setEditing(null); toast('Document updated', 'success') },
    onError: e => toast(e.message, 'error'),
  })

  const deleteMut = useMutation({
    mutationFn: ({ id, reason }) => api.deleteDocument(id, reason),
    onSuccess: () => { qc.invalidateQueries(['documents']); toast('Document moved to bin', 'success') },
    onError: e => toast(e.message, 'error'),
  })

  const reviewMut = useMutation({
    mutationFn: ({ id, data }) => api.reviewDocument(id, data),
    onSuccess: () => { qc.invalidateQueries(['documents']); setReviewing(null); toast('Decision recorded and uploader notified', 'success') },
    onError: e => toast(e.message, 'error'),
  })

  const submitMut = useMutation({
    mutationFn: ({ id, reviewerId }) => api.submitDocumentForReview(id, reviewerId),
    onSuccess: (data) => { 
      qc.invalidateQueries(['documents']); 
      setSubmitting(null); 

      if (data && data.emailStatus === 'failed') {
        toast('Document submitted, but email notification failed. Approver will see it in their Review Center.', 'warning')
      } else {
        toast('Document submitted and reviewer notified', 'success') 
      }
    },
    onError: e => toast(e.message, 'error'),
  })

  const handleDownload = (doc) => {
    if (!doc.file_data) {
      toast('No file content available to download', 'error')
      return
    }

    try {
      const byteCharacters = atob(doc.file_data)
      const byteNumbers = new Array(byteCharacters.length)
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i)
      }
      const byteArray = new Uint8Array(byteNumbers)
      const blob = new Blob([byteArray], { type: doc.content_type || 'application/octet-stream' })
      
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = doc.file_url || `${doc.title}.bin`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      toast(`Downloaded: ${doc.file_url}`, 'success')
    } catch (e) {
      console.error('Download error:', e)
      toast('Failed to process file for download', 'error')
    }
  }

  const filtered = docs.filter(d => {
    // Contributors can only see approved documents
    if ((user?.role === 'Contributor' || user?.role === 'User') && d.status !== 'Approved') {
      return false;
    }
    return d.title.toLowerCase().includes(search.toLowerCase()) || d.category.toLowerCase().includes(search.toLowerCase());
  })

  const statusBadge = (s) => ({
    'Draft': 'badge-neutral', 
    'Under Review': 'badge-info',
    'Approved': 'badge-success', 
    'Needs Improvement': 'badge-warning',
    'Obsolete': 'badge-danger'
  }[s] || 'badge-neutral')

  return (
    <div className="page">
      <div className="page-header flex items-center justify-between">
        <div>
          <h1>Document Library</h1>
          <p>Central repository for all ISMS policies, procedures, and records</p>
        </div>
        {(user?.role === 'Admin' || user?.role === 'Manager') && (
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            <Plus size={15} /> Upload Document
          </button>
        )}
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input
            className="form-input"
            style={{ paddingLeft: 40 }}
            placeholder="Search documents by title or category..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <button className="btn btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Filter size={16} /> Filters
        </button>
      </div>

      <div className="grid-layout" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 20, display: 'grid' }}>
        {isLoading && <div className="empty-state">Loading documents...</div>}
        {filtered.map(doc => (
          <div key={doc.id} className="card shadow-sm" style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="flex items-center justify-between">
              <div style={{ width: 40, height: 40, borderRadius: 8, background: 'var(--accent-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FileText size={20} color="var(--accent)" />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {(user?.role === 'Admin' || user?.role === 'Manager') && (doc.status === 'Draft' || doc.status === 'Needs Improvement') && (
                  <button className="btn btn-primary btn-sm" style={{ fontSize: '0.7rem', padding: '4px 8px' }} onClick={() => setSubmitting(doc)}>
                    Submit for Review
                  </button>
                )}
                <span className={`badge ${statusBadge(doc.status)}`}>{doc.status}</span>
              </div>
            </div>

            <div>
              <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 4 }}>{doc.title}</h3>
              <div className="flex items-center gap-8" style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                <span>{doc.category}</span>
                <span>•</span>
                <span className="font-mono">v{doc.version}</span>
              </div>
            </div>

            <div style={{ marginTop: 'auto', paddingTop: 16, borderTop: '1px solid var(--border)', display: 'flex', justify_content: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                Owner: <span style={{ fontWeight: 500 }}>{doc.owner || '—'}</span>
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                  <button className="btn btn-ghost btn-sm" style={{ padding: 6 }} title="Download" onClick={() => handleDownload(doc)}>
                    <Download size={14} />
                  </button>
                  <button className="btn btn-ghost btn-sm" style={{ padding: 6 }} title="View Document File" onClick={() => setPreviewing(doc)}>
                    <Eye size={16} />
                  </button>
                  {user?.role !== 'Contributor' && user?.role !== 'User' && (
                    <button className="btn btn-ghost btn-sm" style={{ padding: 6, color: 'var(--text-muted)' }} title="Audit Track & Details" onClick={() => setViewing(doc)}>
                      <Clock size={15} />
                    </button>
                  )}
                  {(user?.role === 'Admin' || user?.role === 'Manager') && (
                    <>
                      <button className="btn btn-ghost btn-sm" style={{ padding: 6 }} title="Edit Metadata" onClick={() => setEditing(doc)}>
                        <Edit size={14} />
                      </button>
                      <button className="btn btn-ghost btn-sm" style={{ padding: 6, color: 'var(--danger)' }} title="Delete" onClick={() => setConfirmDelete(doc)}>
                        <Trash2 size={14} />
                      </button>
                    </>
                  )}
                </div>
            </div>
          </div>
        ))}
        {!isLoading && filtered.length === 0 && (
          <div className="empty-state" style={{ gridColumn: '1/-1', padding: 60 }}>
            <Archive size={48} color="var(--border)" />
            <p>No documents found matching your search.</p>
          </div>
        )}
      </div>

      {showModal && (
        <DocModal
          onClose={() => setShowModal(false)}
          onSave={data => createMut.mutate(data)}
          isSaving={createMut.isPending}
        />
      )}

      {viewing && (
        <DocDetailsModal
          doc={viewing}
          onClose={() => setViewing(null)}
        />
      )}

      {previewing && (
        <FilePreviewModal
          doc={previewing}
          onClose={() => setPreviewing(null)}
        />
      )}

      {editing && (
        <DocModal
          doc={editing}
          onClose={() => setEditing(null)}
          onSave={data => updateMut.mutate({ id: editing.id, data })}
          isSaving={updateMut.isPending}
        />
      )}

      {reviewing && (
        <DocReviewModal
          doc={reviewing}
          onClose={() => setReviewing(null)}
          onReview={data => reviewMut.mutate({ id: reviewing.id, data })}
          isReviewing={reviewMut.isPending}
        />
      )}

      {submitting && (
        <SubmitReviewModal
          doc={submitting}
          users={users}
          onClose={() => setSubmitting(null)}
          onSubmit={reviewerId => submitMut.mutate({ id: submitting.id, reviewerId })}
          isSubmitting={submitMut.isPending}
        />
      )}

      <ConfirmModal
        isOpen={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={(reason) => deleteMut.mutate({ id: confirmDelete.id, reason })}
        title="Move to Bin?"
        message={`Delete "${confirmDelete?.title}"? It will be moved to the Recycle Bin and can be restored later.`}
        showReason={true}
      />
    </div>
  )
}
